import { TextIntegrationAddModal } from '@capturi/integrations'
import request from '@capturi/request'
import { useOrganization } from '@capturi/stores'
import { Spinner } from '@capturi/ui-components'
import {
  Box,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'
import { Trans } from '@lingui/macro'
import { useQuery } from '@tanstack/react-query'
import React, { ReactElement, useState } from 'react'
import { RiArrowRightSLine } from 'react-icons/ri'
import AddBoostAi from './boostAi/AddBoostAi'
import AddGenesysChat from './genesysChat/AddGenesysChat'
import { getTextColor } from './getTextColor'

type Props = { onClose: () => void }

type Integration = {
  channel: 'text'
  color: string
  type: string
  name: string
}

const legacy = ['genesysChat', 'boostAi']

const focus = (ref: HTMLInputElement | null) => {
  ref?.focus()
}

export const AddImporterModal: React.FC<Props> = ({ onClose }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['superpowers', 'textIntegrations', 'types'],
    queryFn: () =>
      request.get<{ integrationTypes: Integration[] }>(
        'importer/mr-integration/types?channel=text',
      ),
  })

  const [term, setTerm] = useState<string>('')

  const [add, setAdd] = useState<string | null>(null)
  const [addV2, setAddV2] = useState<Integration | null>(null)
  const { name: organizationName, uid: organizationUid } = useOrganization()

  const renderIntegrationButton = (config: Integration): ReactElement => {
    return (
      <Flex
        justifyContent="space-between"
        alignItems="center"
        as="button"
        p="2"
        _hover={{
          bg: 'primary.50',
        }}
        width="100%"
        borderColor="border.light"
        borderBottomWidth="1px"
        key={config.type}
        onClick={() => {
          if (legacy.includes(config.type)) {
            setAdd(config.type)
          } else {
            setAddV2(config)
          }
        }}
      >
        <Flex alignItems="center">
          <Box
            w="28px"
            h="28px"
            fontSize="1.2rem"
            bg={config.color}
            mr="4"
            borderRadius="4"
            justifyContent="center"
            alignItems="center"
            color={getTextColor(config.color)}
          >
            {config.name[0]}
          </Box>
          {config.name}
        </Flex>
        <RiArrowRightSLine color="greys" size="25" />
      </Flex>
    )
  }

  if (add === 'genesysChat') return <AddGenesysChat onClose={onClose} />
  if (add === 'boostAi') return <AddBoostAi onClose={onClose} />

  if (addV2)
    return (
      <TextIntegrationAddModal
        integrationType={addV2.type}
        organizationName={organizationName}
        organizationUid={organizationUid}
        url={addV2.type}
        onClose={onClose}
      />
    )

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalOverlay>
        <ModalContent>
          <Flex direction="column">
            <ModalHeader>
              <Trans>Add Importer</Trans>
            </ModalHeader>
          </Flex>
          <ModalCloseButton />
          <ModalBody>
            <Input
              onChange={(e) => setTerm(e.target.value)}
              value={term}
              placeholder="Search"
              mb="4"
              ref={focus}
            />
            {error && (
              <Text>
                {error.name} - {error.message}
              </Text>
            )}
            {isLoading && <Spinner />}
            {data?.integrationTypes.map(renderIntegrationButton)}
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}
